// AI 计算服务（WASM 版本）- 使用 Web Worker + WebAssembly
// 仅在构建配置选择 wasm 模式时通过别名作为 `@/services/AIService` 被引用

import * as Sentry from "@sentry/react"
import type { AIGameState } from "./goUtils"
// 直接导入 WASM 文件（Rspack 会处理为固定文件名的 URL）
import wasmUrl from "../../wasm/wasm_go_ai_bg.wasm"

// 声明通过 DefinePlugin 注入的 worker 代码常量（生产模式）
declare const __WORKER_CODE__: string | undefined

// 简单能力检测：是否支持 Worker 和 WebAssembly
export const canUseWasmAI = (): boolean => {
    if (typeof window === "undefined") return false

    const hasWorker = typeof Worker !== "undefined"
    const hasWasm = typeof WebAssembly !== "undefined"
    const supported = hasWorker && hasWasm

    if (!supported) {
        Sentry.captureMessage("WASM_ERROR", {
            level: "warning",
            tags: { wasm_error_type: "unsupported" },
            extra: { hasWorker, hasWasm },
        })
    }

    return supported
}

// 创建 Web Worker（仅供 WASM 版 AI 使用）
let worker: Worker | null = null

const getWorker = (): Worker => {
    if (!worker) {
        try {
            // 检查是否有注入的 worker 代码（生产模式）
            const workerCode =
                typeof __WORKER_CODE__ !== "undefined" ? __WORKER_CODE__ : ""

            if (workerCode) {
                // 生产模式：使用内联 Worker
                const blob = new Blob([workerCode], {
                    type: "application/javascript",
                })
                const workerUrl = URL.createObjectURL(blob)
                worker = new Worker(workerUrl)
                // 发送 WASM 文件路径给 Worker
                // wasmUrl 是 Rspack 处理后的 URL（固定文件名 + publicPath）
                worker.postMessage({
                    type: "initWasmPath",
                    wasmPath: wasmUrl,
                })

                worker.addEventListener(
                    "message",
                    () => {
                        setTimeout(() => {
                            URL.revokeObjectURL(workerUrl)
                        }, 1000)
                    },
                    { once: true }
                )
            } else {
                // 开发模式：直接引入 worker 文件
                worker = new Worker(
                    new URL("../../workers/goAI.worker.ts", import.meta.url),
                    { type: "module" }
                )

                // 开发模式也传递 WASM URL
                worker.postMessage({
                    type: "initWasmPath",
                    wasmPath: wasmUrl,
                })
            }
        } catch (error) {
            throw new Error("Web Worker不支持，请使用现代浏览器")
        }
    }
    return worker
}

// 使用 Web Worker + WASM 计算 AI 落子
export const calculateAIMove = (
    state: AIGameState
): Promise<{ row: number; col: number } | null> => {
    // 能力检测：不支持 WASM / Worker 时直接抛错，让上层兜底
    if (!canUseWasmAI()) {
        return Promise.reject(
            new Error("WASM / Web Worker not supported by this environment")
        )
    }

    return new Promise((resolve, reject) => {
        const worker = getWorker()

        // 设置超时（低端机上避免等待过久）
        const timeout = setTimeout(() => {
            reject(new Error("AI计算超时"))
        }, 5000) // 5秒超时

        // 监听 worker 消息
        const messageHandler = (event: MessageEvent) => {
            const { type, move, error } = event.data

            if (type === "moveResult") {
                clearTimeout(timeout)
                worker.removeEventListener("message", messageHandler)
                resolve(move)
            } else if (type === "error") {
                clearTimeout(timeout)
                worker.removeEventListener("message", messageHandler)
                const err = new Error(error)
                Sentry.captureMessage("WASM_ERROR", {
                    level: "warning",
                    tags: { wasm_error_type: "runtime" },
                    extra: { detail: error },
                })
                reject(err)
            }
        }

        worker.addEventListener("message", messageHandler)

        // 发送计算请求
        worker.postMessage({
            type: "calculateMove",
            state,
        })
    })
}

// 清理 worker
export const cleanupWorker = () => {
    if (worker) {
        worker.terminate()
        worker = null
    }
}
